import {
  Exercise,
  CreateExerciseInput,
  UpdateExerciseInput,
  ExerciseFilters,
  ExercisesResponse,
  ExerciseOperationResult,
  ExercisesApiResponse,
  MuscleGroup,
  UpdateActivityLogInput,
} from "../types/exercise";
import { ApiService } from "./apiService";
import { failure } from "./serviceHelpers";
import { fetchAllPages, unwrapPaginated } from "../utils/paginationHelper";
import { logger } from "../utils/logger";

export class ExerciseService {
  // CREATE - Add a new exercise
  static async createExercise(
    exerciseData: CreateExerciseInput
  ): Promise<ExerciseOperationResult> {
    try {
      const response = await ApiService.post("/exercises", exerciseData);
      const result = await ApiService.handleResponse<{ data: Exercise }>(
        response
      );

      if (result.data) {
        return { success: true, data: result.data };
      }

      logger.error("Unexpected create exercise API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // READ - Get all exercises with pagination and filters
  static async getExercises(
    page: number = 1,
    filters: ExerciseFilters = {}
  ): Promise<ExercisesResponse | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
      });

      if (filters.search) {
        params.append("search", filters.search);
      }

      const response = await ApiService.get(`/exercises?${params.toString()}`);
      const result = await ApiService.handleResponse<ExercisesApiResponse>(
        response
      );

      const paged = unwrapPaginated(result, "exercises");

      if (!paged) return null;

      return {
        exercises: paged.items,

        total: paged.total,

        page: paged.page,

        limit: paged.limit,
      };
    } catch (err: any) {
      logger.error("Error fetching exercises:", err);
      return null;
    }
  }

  // READ - Get all exercises (without pagination)
  static async getAllExercises(): Promise<Exercise[]> {
    try {
      return await fetchAllPages<Exercise>("/exercises");
    } catch (err: any) {
      logger.error("Error fetching exercises:", err);
      return [];
    }
  }

  // READ - Get a single exercise by ID
  static async getExerciseById(exerciseId: number): Promise<Exercise | null> {
    try {
      const response = await ApiService.get(`/exercises/${exerciseId}`);
      const result = await ApiService.handleResponse<{ data: Exercise }>(
        response
      );

      if (result.data) {
        return result.data;
      }

      logger.error("Unexpected exercise API response format:", result);
      return null;
    } catch (err: any) {
      logger.error("Error fetching exercise:", err);
      return null;
    }
  }

  // UPDATE - Update exercise information
  static async updateExercise(
    exerciseId: number,
    updates: UpdateExerciseInput
  ): Promise<ExerciseOperationResult> {
    try {
      const response = await ApiService.patch(
        `/exercises/${exerciseId}`,
        updates
      );
      const result = await ApiService.handleResponse<{ data: Exercise }>(
        response
      );

      if (result.data) {
        return { success: true, data: result.data };
      }

      logger.error("Unexpected update exercise API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // DELETE - Delete exercise
  static async deleteExercise(
    exerciseId: number
  ): Promise<ExerciseOperationResult> {
    try {
      const response = await ApiService.delete(`/exercises/${exerciseId}`);
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // PROGRAMS - Get exercise with assigned programs
  static async getExerciseWithPrograms(
    exerciseId: number
  ): Promise<Exercise | null> {
    try {
      const exercise = await this.getExerciseById(exerciseId);
      return exercise;
    } catch (err: any) {
      logger.error("Error fetching exercise with programs:", err);
      return null;
    }
  }

  // PROGRAMS - Get all programs for assignment (programs not assigned to this exercise)
  static async getAvailableProgramsForExercise(
    exerciseId: number
  ): Promise<any[]> {
    try {
      // Get the exercise with current programs
      const exercise = await this.getExerciseById(exerciseId);
      const assignedProgramIds =
        exercise?.training_programs?.map((program) => program.id) || [];

      // Get all programs using pagination helper
      const allPrograms = await fetchAllPages<any>("/admin/training-programs");

      // Filter out programs already assigned to this exercise
      return allPrograms.filter(
        (program: any) => !assignedProgramIds.includes(program.id)
      );
    } catch (err: any) {
      logger.error("Error fetching available programs for exercise:", err);
      return [];
    }
  }

  // SEARCH - Search exercises with pagination
  static async searchExercises(
    searchTerm: string,
    page: number = 1
  ): Promise<{ data: ExercisesApiResponse } | null> {
    try {
      const params = new URLSearchParams({
        searchTerm: searchTerm,
        page: page.toString(),
      });

      const response = await ApiService.get(
        `/exercises/search?${params.toString()}`
      );
      const result = await ApiService.handleResponse<ExercisesApiResponse>(
        response
      );

      if (!unwrapPaginated(result, "exercise search")) return null;

      return { data: result };
    } catch (err: any) {
      logger.error("Error searching exercises:", err);
      return null;
    }
  }

  // MUSCLE GROUPS - Get all muscle groups
  static async getAllMuscleGroups(): Promise<MuscleGroup[]> {
    try {
      const response = await ApiService.get("/muscle-groups");
      const result = await ApiService.handleResponse<{ data: MuscleGroup[] }>(
        response
      );

      if (result.data && Array.isArray(result.data)) {
        return result.data;
      }

      logger.error("Unexpected muscle groups API response format:", result);
      return [];
    } catch (err: any) {
      logger.error("Error fetching muscle groups:", err);
      return [];
    }
  }

  // TRAINING DAYS - Assign exercise to training day
  static async assignExerciseToTrainingDay(
    exerciseId: number,
    trainingDayId: number,
    isPersistent: boolean,
    userId?: number
  ): Promise<ExerciseOperationResult> {
    try {
      const payload: any = {
        exercise_id: exerciseId,
        training_day_id: trainingDayId,
        is_persistent: isPersistent,
      };

      if (userId !== null && userId !== undefined) {
        payload.user_id = userId;
      }

      const response = await ApiService.post(
        "/admin/user-exercise-activity-logs/add-new",
        payload
      );
      const result = await ApiService.handleResponse<any>(response);

      return { success: true, data: result };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // TRAINING DAYS - Unassign exercise from training day
  static async unassignExerciseFromTrainingDay(
    activityLogId: number,
    isPersistent: boolean = false
  ): Promise<ExerciseOperationResult> {
    try {
      const response = await ApiService.delete(
        `/user-exercise-activity-logs/${activityLogId}`,
        { is_persistent: isPersistent }
      );
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // TRAINING DAYS - Update sets / rest / RIR / RPE / notes on one activity log
  static async updateActivityLog(
    activityLogId: number,
    payload: UpdateActivityLogInput
  ): Promise<ExerciseOperationResult> {
    try {
      const response = await ApiService.patch(
        `/user-exercise-activity-logs/${activityLogId}`,
        payload
      );
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "Failed to save sets data. Please try again.");
    }
  }

  // TRAINING DAYS - Update exercise positions
  static async updateExercisePositions(
    trainingDayId: number,
    positions: Record<number, number>,
    isPersistent: boolean = false,
    isAdmin: boolean = true
  ): Promise<ExerciseOperationResult> {
    try {
      const response = await ApiService.post(
        "/user-exercise-activity-logs/update-positions",
        {
          positions: positions,
          training_day_id: trainingDayId,
          is_persistent: isPersistent,
          is_admin: isAdmin,
        }
      );
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }
}
